import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CalendarDays,
  Camera,
  ChevronRight,
  MapPin,
  MessageCircle,
  ShieldCheck,
  Star,
  Trophy,
  UsersRound,
} from 'lucide-react';
import { site } from './content/site';

type EventItem = (typeof site.events)[number];

const featureIcons = {
  users: UsersRound,
  trophy: Trophy,
  shield: ShieldCheck,
  calendar: CalendarDays,
};

const eventHashPrefix = 'evento/';

function readEventSlugFromHash() {
  if (typeof window === 'undefined') {
    return null;
  }

  const hash = window.location.hash.replace(/^#/, '');

  if (!hash.startsWith(eventHashPrefix)) {
    return null;
  }

  const slug = hash.slice(eventHashPrefix.length);

  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
}

function App() {
  const [activeEventSlug, setActiveEventSlug] = useState<string | null>(() =>
    readEventSlugFromHash(),
  );

  useEffect(() => {
    const syncActiveEvent = () => setActiveEventSlug(readEventSlugFromHash());

    window.addEventListener('hashchange', syncActiveEvent);
    syncActiveEvent();

    return () => window.removeEventListener('hashchange', syncActiveEvent);
  }, []);

  const activeEvent = useMemo(
    () => site.events.find((event) => event.slug === activeEventSlug),
    [activeEventSlug],
  );

  return (
    <>
      <header className="site-header" aria-label="Navegación principal">
        <a className="brand" href="#inicio" aria-label={site.name}>
          <img className="brand-logo" src={site.logo} alt={site.name} />
        </a>
        <nav className="nav-links">
          {site.nav.map((item) => (
            <a href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <a className="header-cta" href={site.contact.whatsappUrl}>
          <MessageCircle size={18} aria-hidden="true" />
          Agendar clase
        </a>
      </header>

      <main>
        <section className="hero" id="inicio" aria-labelledby="hero-title">
          <div className="hero-media" aria-hidden="true">
            <img src={site.hero.image} alt="" />
          </div>
          <div className="hero-glow" aria-hidden="true" />
          <div className="hero-content page-shell">
            <h1 id="hero-title" className="hero-title">
              <img className="hero-logo" src={site.logo} alt={site.name} />
            </h1>
            <p className="hero-copy">{site.hero.copy}</p>
            <div className="hero-actions" aria-label="Acciones principales">
              <a className="button button-primary" href={site.contact.whatsappUrl}>
                <MessageCircle size={20} aria-hidden="true" />
                {site.hero.primaryCta}
              </a>
              <a className="button button-secondary" href="#horarios">
                {site.hero.secondaryCta}
                <ChevronRight size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="section section-features page-shell" id="clases">
          <div className="center-heading">
            <p className="section-label">Grupos por edad</p>
            <h2>Clases para cada etapa</h2>
          </div>
          <div className="feature-grid">
            {site.features.map((feature) => {
              const Icon = featureIcons[feature.icon as keyof typeof featureIcons];
              return (
                <article className="feature-card" key={feature.title}>
                  <span className="icon-tile">
                    <Icon size={24} aria-hidden="true" />
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="section section-styles page-shell" id="estilos">
          <div className="split-heading">
            <div>
              <p className="section-label">Nuestras clases</p>
              <h2>Disciplinas que impartimos</h2>
            </div>
            <a className="text-link" href="#horarios">
              Ver horario
              <ChevronRight size={17} aria-hidden="true" />
            </a>
          </div>
          <div className="style-grid">
            {site.styles.map((style) => (
              <article className="style-card" key={style.title}>
                <img
                  src={style.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: style.imagePosition }}
                />
                <div className="image-shade" aria-hidden="true" />
                <div className="style-card-content">
                  <p>{style.level}</p>
                  <h3>{style.title}</h3>
                  <span>{style.body}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="schedule-band" id="horarios">
          <div className="page-shell schedule-inner">
            <div>
              <p className="section-label">{site.schedule.label}</p>
              <h2>{site.schedule.title}</h2>
              <p>{site.schedule.copy}</p>
            </div>
            <a className="button schedule-button" href={site.contact.whatsappUrl}>
              <MessageCircle size={18} aria-hidden="true" />
              {site.schedule.cta}
            </a>
          </div>
        </section>

        <section className="section section-events page-shell" id="eventos">
          {activeEvent ? (
            <EventDetail event={activeEvent} />
          ) : (
            <>
              <div className="split-heading events-heading">
                <div>
                  <p className="section-label">Eventos</p>
                  <h2>Escenario y comunidad</h2>
                  <p>
                    Momentos reales de alumnas Legacy Dance en competencias, showcases y
                    presentaciones especiales.
                  </p>
                </div>
                <a className="text-link" href={site.contact.instagramUrl}>
                  Ver Instagram
                  <ChevronRight size={17} aria-hidden="true" />
                </a>
              </div>
              <div className="event-grid">
                {site.events.map((event) => (
                  <a
                    className="event-card"
                    href={`#evento/${event.slug}`}
                    key={event.slug}
                    onClick={() => setActiveEventSlug(event.slug)}
                    aria-label={`Ver evento ${event.title}`}
                  >
                    <img
                      src={event.coverImage}
                      alt={event.coverAlt}
                      loading="lazy"
                      decoding="async"
                      style={{ objectPosition: event.coverPosition }}
                    />
                    <div className="image-shade" aria-hidden="true" />
                    <div className="event-card-content">
                      <span>{event.eyebrow}</span>
                      <h3>{event.title}</h3>
                      <p>{event.excerpt}</p>
                      <div className="event-card-meta">
                        <small>{event.date}</small>
                        <small>{event.location}</small>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="section page-shell" id="testimonios">
          <div className="center-heading">
            <p className="section-label">Testimonios</p>
            <h2>Lo que dice nuestra comunidad</h2>
          </div>
          <div className="testimonial-grid">
            {site.testimonials.map((testimonial) => (
              <article className="testimonial-card" key={testimonial.name}>
                <div className="stars" aria-label="Cinco estrellas">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Star size={16} fill="currentColor" key={index} aria-hidden="true" />
                  ))}
                </div>
                <span className="quote-mark" aria-hidden="true">
                  "
                </span>
                <p>{testimonial.quote}</p>
                <div className="testimonial-person">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.meta}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="contact-section" id="contacto">
          <div className="page-shell contact-grid">
            <div>
              <p className="section-label">Contáctanos</p>
              <h2>{site.contact.title}</h2>
              <p>{site.contact.copy}</p>
              <a className="button whatsapp-button" href={site.contact.whatsappUrl}>
                <MessageCircle size={20} aria-hidden="true" />
                Escribir por WhatsApp
              </a>
            </div>
            <div className="contact-list">
              <a href={site.contact.whatsappUrl} className="contact-row">
                <span className="icon-tile">
                  <MessageCircle size={22} aria-hidden="true" />
                </span>
                <span>
                  <small>WhatsApp</small>
                  <strong>{site.contact.whatsappDisplay}</strong>
                </span>
                <ChevronRight size={20} aria-hidden="true" />
              </a>
              <a href={site.contact.instagramUrl} className="contact-row">
                <span className="icon-tile">
                  <Camera size={22} aria-hidden="true" />
                </span>
                <span>
                  <small>Instagram</small>
                  <strong>{site.contact.instagram}</strong>
                </span>
                <ChevronRight size={20} aria-hidden="true" />
              </a>
              <div className="contact-row">
                <span className="icon-tile">
                  <MapPin size={22} aria-hidden="true" />
                </span>
                <span>
                  <small>Ubicación</small>
                  <strong>{site.contact.location}</strong>
                </span>
                <ChevronRight size={20} aria-hidden="true" />
              </div>
              <div className="reminder-card">
                <strong>Primera clase muestra gratis</strong>
                <p>{site.contact.reminder}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-credit">
          <span>{site.footer.developedBy}</span>
          <a href={site.footer.studioUrl} target="_blank" rel="noopener noreferrer">
            <img src={site.footer.studioLogo} alt={site.footer.studioName} />
          </a>
        </div>
        <p>© 2026 {site.name} · {site.contact.location} · Todos los derechos reservados</p>
        <div className="footer-social">
          <a href={site.contact.instagramUrl} aria-label="Instagram">
            <Camera size={20} aria-hidden="true" />
          </a>
          <a href={site.contact.whatsappUrl} aria-label="WhatsApp">
            <MessageCircle size={20} aria-hidden="true" />
          </a>
        </div>
      </footer>
    </>
  );
}

function EventDetail({ event }: { event: EventItem }) {
  return (
    <article className="event-detail">
      <div className="event-detail-copy">
        <a className="text-link back-link" href="#eventos">
          <ArrowLeft size={17} aria-hidden="true" />
          Todos los eventos
        </a>
        <p className="section-label">{event.eyebrow}</p>
        <h2>{event.title}</h2>
        <div className="event-detail-meta" aria-label="Datos del evento">
          <span>
            <CalendarDays size={17} aria-hidden="true" />
            {event.date}
          </span>
          <span>
            <MapPin size={17} aria-hidden="true" />
            {event.location}
          </span>
        </div>
        <div className="event-body">
          {event.body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
      <div className="event-detail-media">
        <img
          src={event.coverImage}
          alt={event.coverAlt}
          loading="eager"
          decoding="async"
          style={{ objectPosition: event.coverPosition }}
        />
      </div>
      <div className="event-gallery" aria-label={`Galería de ${event.title}`}>
        {event.gallery.map((image) => (
          <img
            src={image.src}
            alt={image.alt}
            key={image.src}
            loading="lazy"
            decoding="async"
            style={{ objectPosition: image.position }}
          />
        ))}
      </div>
    </article>
  );
}

export default App;
